import { FormattedMessage, useIntl } from 'react-intl'
import cn from 'classnames'
import { Form, Skeleton } from 'antd'
import Title from 'shared/components/typography/Title'
import Text from 'shared/components/typography/Text'
import { PaymentType } from '../../api/streamer/types'
import { FormValues } from '../../types'
import { lang } from './lang'
import s from './s.module.css'
import { checkLongDisplay, getDisplayValue } from './utils'
import { formKeys } from '@/pages/Donation/components/DonationForm/constants.ts'

type Props = {
  paymentType: PaymentType
  baseCurrencyCode?: string
  cryptoAssetCode?: string
  finalAmount?: string | number | null
  fee?: string
  isLoading: boolean
}

export default function TotalAmount({
  paymentType,
  cryptoAssetCode,
  baseCurrencyCode,
  finalAmount,
  fee,
  isLoading,
}: Props) {
  const intl = useIntl()

  const isIncludeFees = Form.useWatch<FormValues[typeof formKeys.isIncludeFee]>(formKeys.isIncludeFee)

  const isFiat = paymentType === 'fiat'
  const displayedCurrencyCode = isFiat ? baseCurrencyCode : cryptoAssetCode
  const isManualCryptoCurrencyDisplay = !isFiat

  const feeDisplay = getDisplayValue({
    intl,
    value: fee,
    isFiat,
    currencyCode: displayedCurrencyCode,
  })

  const amountDisplay = getDisplayValue({
    intl,
    value: finalAmount,
    isFiat,
    currencyCode: displayedCurrencyCode,
    showNullAsZero: true,
    withManualCryptoCurrency: isManualCryptoCurrencyDisplay,
  })

  const isVertical = checkLongDisplay(amountDisplay, feeDisplay)

  return (
    <div className={cn(s.wrap, { [s.withFee]: !!feeDisplay, [s.vertical]: isVertical })}>
      <Title fontSize={16} fontType="title" level={2} className={s.title}>
        <FormattedMessage {...lang.totalAmount} />
      </Title>
      {feeDisplay && !isLoading && isIncludeFees && (
        <div className={s.fee}>
          <Text fontType="description" type="tertiary">
            <FormattedMessage {...lang.youCover} values={{ value: feeDisplay }} />
          </Text>
        </div>
      )}
      <div className={s.amountValue}>
        {isLoading ? (
          <Skeleton.Button active style={{ height: '40px', minWidth: '100px' }} />
        ) : (
          <>
            <Text fontType="header" fontSize={1} type="primary">
              {amountDisplay}
            </Text>
            {isManualCryptoCurrencyDisplay && (
              <Text fontType="header" fontSize={3} type="primary">
                {' '}
                {displayedCurrencyCode}
              </Text>
            )}
          </>
        )}
      </div>
    </div>
  )
}
